import React from "react";

export function getRoundScoreboard(round) {
  const gameInfo = round?.last_game_info || {};
  const rawPlayers = gameInfo.players;
  const players = rawPlayers && typeof rawPlayers === "object" && !Array.isArray(rawPlayers)
    ? Object.entries(rawPlayers)
      .map(([name, value]) => ({ name, score: Number(value) }))
      .filter((player) => player.name && Number.isFinite(player.score))
      .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
    : [];
  const finished = gameInfo.is_finished === true;
  const winningScore = finished && players.length ? players[0].score : null;
  const winners = winningScore === null
    ? []
    : players.filter((player) => player.score === winningScore).map((player) => player.name);

  return { players, finished, winners };
}

export function formatPlayDuration(totalSeconds) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes} min`;
  return seconds ? `${seconds} sec` : "Less than a minute";
}

function formatPlayedAt(value) {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 4h8v3.5a4 4 0 0 1-8 0V4Zm4 7.5V16m-3 4h6m-5-4h4M8 6H5.5v1.5A3.5 3.5 0 0 0 9 11m7-5h2.5v1.5A3.5 3.5 0 0 1 15 11" />
    </svg>
  );
}

function RoundCard({ round, roundNumber }) {
  const { players, finished, winners } = getRoundScoreboard(round);
  const winnerText = winners.length > 1
    ? `Winners: ${winners.join(", ")}`
    : winners.length === 1
      ? `Winner: ${winners[0]}`
      : finished
        ? "Round finished"
        : "Round in progress";

  return (
    <article className="rounded-2xl border border-white/10 bg-slate-950/35 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-bold text-white">Round {roundNumber}</h4>
          <p className="mt-1 text-xs text-indigo-100/45">{formatPlayedAt(round.created_at)}</p>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold ${
          winners.length
            ? "border-amber-300/30 bg-amber-300/10 text-amber-100"
            : "border-white/10 bg-white/5 text-indigo-100/60"
        }`}>
          {winners.length ? <TrophyIcon /> : null}
          {winnerText}
        </div>
      </div>

      {players.length ? (
        <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
          {players.map((player, index) => {
            const isWinner = winners.includes(player.name);
            return (
              <div key={player.name} className={`flex items-center gap-3 border-b border-white/5 px-3 py-2.5 last:border-b-0 ${isWinner ? "bg-amber-300/10" : "bg-white/[0.03]"}`}>
                <span className="w-6 text-xs font-bold tabular-nums text-indigo-100/40">{index + 1}</span>
                <span className="min-w-0 flex-1 truncate font-semibold text-white">{player.name}</span>
                {isWinner ? <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-200">Winner</span> : null}
                <strong className="tabular-nums text-sky-200">{player.score}</strong>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-4 rounded-xl bg-white/[0.03] px-3 py-3 text-sm text-indigo-100/55">
          No player scores were recorded for this round.
        </p>
      )}
    </article>
  );
}

function formatScore(value) {
  const score = Number(value);
  if (!Number.isFinite(score)) return "0";
  return Number.isInteger(score) ? String(score) : score.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function SessionCard({ session }) {
  return (
    <article className="overflow-hidden rounded-3xl border border-white/10 bg-indigo-950/40 shadow-xl shadow-slate-950/25">
      <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center">
        {session.game?.cover_image ? (
          <img src={session.game.cover_image} alt="" className="h-24 w-full rounded-2xl object-cover sm:w-36" />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-black text-white">{session.game?.title || "Game session"}</h3>
              <p className="mt-1 text-sm text-indigo-100/55">{formatPlayedAt(session.start_time)}</p>
            </div>
            <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] ${session.is_active ? "border-emerald-300/25 bg-emerald-300/10 text-emerald-100" : "border-white/10 bg-white/5 text-indigo-100/60"}`}>
              {session.is_active ? "Active" : "Finished"}
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-indigo-100/65">
            <span className="rounded-full bg-white/5 px-3 py-1.5">{formatPlayDuration(session.duration_seconds)}</span>
            <span className="rounded-full bg-white/5 px-3 py-1.5">{session.rounds?.length || 0} {(session.rounds?.length || 0) === 1 ? "round" : "rounds"}</span>
          </div>
        </div>
      </div>

      <div className="space-y-3 border-t border-white/10 bg-slate-950/15 p-5">
        {session.rounds.map((round, index) => (
          <RoundCard key={round.id} round={round} roundNumber={index + 1} />
        ))}
      </div>
    </article>
  );
}

function TournamentRunCard({ run }) {
  const tournament = run.tournament || {};
  const winners = run.overall_winners || [];
  const games = tournament.games || [];
  const sessions = run.sessions || [];
  const status = run.status ? `${run.status[0].toUpperCase()}${run.status.slice(1)}` : "Tournament";
  const winnerLabel = winners.length > 1 ? "Overall winners" : "Overall winner";

  return (
    <article className="overflow-hidden rounded-3xl border border-fuchsia-300/20 bg-indigo-950/50 shadow-xl shadow-fuchsia-950/20">
      <div className="bg-gradient-to-r from-fuchsia-500/15 via-indigo-500/10 to-sky-500/10 p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="text-xs font-black uppercase tracking-[0.24em] text-fuchsia-200/80">Tournament run</span>
            <h3 className="mt-2 text-2xl font-black text-white">{tournament.name || "Tournament"}</h3>
            <p className="mt-1 text-sm text-indigo-100/55">{formatPlayedAt(run.started_at || run.created_at)}</p>
          </div>
          <span className="rounded-full border border-fuchsia-200/20 bg-fuchsia-300/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-fuchsia-100">
            {status}
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold text-indigo-100/70">
          <span className="rounded-full bg-white/5 px-3 py-1.5">{tournament.score_mode || "Tournament"} scoring</span>
          <span className="rounded-full bg-white/5 px-3 py-1.5">{tournament.team_mode || "Individual"}</span>
          <span className="rounded-full bg-white/5 px-3 py-1.5">{games.length} {games.length === 1 ? "game" : "games"}</span>
        </div>

        {winners.length ? (
          <div className="mt-5 inline-flex max-w-full items-center gap-3 rounded-2xl border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-amber-100">
            <TrophyIcon />
            <div className="min-w-0">
              <div className="text-[11px] font-black uppercase tracking-[0.2em] text-amber-200/70">{winnerLabel}</div>
              <div className="truncate font-black">{winners.map((winner) => winner.name).join(", ")}</div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="border-t border-white/10 p-5 sm:p-6">
        <h4 className="text-sm font-black uppercase tracking-[0.18em] text-indigo-100/65">Games in tournament</h4>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {games.map((game, index) => (
            <div key={`${game.id}-${index}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-fuchsia-300/10 text-xs font-black text-fuchsia-100">{index + 1}</span>
              {game.cover_image ? <img src={game.cover_image} alt="" className="h-11 w-14 rounded-lg object-cover" /> : null}
              <span className="min-w-0 truncate font-bold text-white">{game.title}</span>
            </div>
          ))}
        </div>
      </div>

      {run.standings?.length ? (
        <div className="border-t border-white/10 bg-slate-950/20 p-5 sm:p-6">
          <h4 className="text-sm font-black uppercase tracking-[0.18em] text-indigo-100/65">Overall standings</h4>
          <div className="mt-3 overflow-hidden rounded-2xl border border-white/10">
            {run.standings.map((entry, index) => {
              const isWinner = winners.some((winner) => winner.id === entry.id);
              return (
                <div key={entry.id} className={`flex items-center gap-3 border-b border-white/5 px-4 py-3 last:border-b-0 ${isWinner ? "bg-amber-300/10" : "bg-white/[0.03]"}`}>
                  <span className="w-6 text-xs font-bold tabular-nums text-indigo-100/45">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate font-semibold text-white">{entry.name}</span>
                  {isWinner ? <span className="text-xs font-black uppercase tracking-[0.16em] text-amber-200">Winner</span> : null}
                  <strong className="tabular-nums text-sky-200">{formatScore(entry.score)}</strong>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="space-y-4 border-t border-white/10 p-5 sm:p-6">
        <h4 className="text-sm font-black uppercase tracking-[0.18em] text-indigo-100/65">Played games and rounds</h4>
        {sessions.map((session, sessionIndex) => (
          <section key={session.id || session.session_id} className="rounded-2xl border border-white/10 bg-slate-950/25 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h5 className="font-black text-white">Game {session.tournament_step === null || session.tournament_step === undefined ? sessionIndex + 1 : session.tournament_step + 1}: {session.game?.title || "Game"}</h5>
                <p className="mt-1 text-xs text-indigo-100/45">{formatPlayedAt(session.start_time)}</p>
              </div>
              {session.attempt_number > 1 ? <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-indigo-100/65">Attempt {session.attempt_number}</span> : null}
            </div>
            <div className="mt-4 space-y-3">
              {session.rounds.map((round, index) => <RoundCard key={round.id} round={round} roundNumber={index + 1} />)}
            </div>
          </section>
        ))}
      </div>
    </article>
  );
}

export default function PlaytimeHistory({ sessions, tournamentRuns = [], isPending, error }) {
  if (isPending) {
    return <div className="mt-5 rounded-3xl border border-white/10 bg-indigo-950/35 p-6 text-indigo-100/65">Loading play history...</div>;
  }

  if (error) {
    return <div className="mt-5 rounded-3xl border border-rose-300/25 bg-rose-500/10 p-6 text-rose-100">Could not load your play history.</div>;
  }

  if (!sessions.length && !tournamentRuns.length) {
    return <div className="mt-5 rounded-3xl border border-dashed border-sky-300/25 bg-sky-300/5 p-6 text-indigo-100/65">Your completed game sessions will appear here.</div>;
  }

  const entries = [
    ...sessions.map((session) => ({ type: "session", data: session, playedAt: session.start_time })),
    ...tournamentRuns.map((run) => ({ type: "tournament", data: run, playedAt: run.started_at || run.created_at })),
  ].sort((a, b) => new Date(b.playedAt || 0) - new Date(a.playedAt || 0));

  return (
    <div className="mt-5 space-y-5">
      {entries.map((entry) => entry.type === "tournament"
        ? <TournamentRunCard key={`tournament-${entry.data.id}`} run={entry.data} />
        : <SessionCard key={`session-${entry.data.id || entry.data.session_id}`} session={entry.data} />)}
    </div>
  );
}
